import { useEffect, useState } from 'react';
import throttle from 'lodash.throttle';

export type WindowSize = { width: number; height: number };

export type UseWindowSizeParams = {
  throttle?: number;
  onResize?: (size: WindowSize) => void;
};

const defaultThrottle = 100;

const defaultParams: UseWindowSizeParams = {
  throttle: defaultThrottle,
  onResize: () => null,
};

export const useWindowSize = (params: UseWindowSizeParams = defaultParams) => {
  const [windowSize, setWindowSize] = useState<WindowSize>(() => ({
    width: window.innerWidth,
    height: window.innerHeight,
  }));

  useEffect(() => {
    const sizeWatcher = () => {
      const size: WindowSize = {
        width: window.innerWidth,
        height: window.innerHeight,
      };

      setWindowSize(size);

      params.onResize?.(size);
    };

    const throttledSizeWatcher = throttle(sizeWatcher, params.throttle ?? defaultThrottle);

    window.addEventListener('resize', throttledSizeWatcher);
    window.addEventListener('orientationchange', throttledSizeWatcher);
    window.screen.orientation?.addEventListener('change', throttledSizeWatcher);

    sizeWatcher();

    return () => {
      window.removeEventListener('resize', throttledSizeWatcher);
      window.removeEventListener('orientationchange', throttledSizeWatcher);
      window.screen.orientation?.removeEventListener('change', throttledSizeWatcher);
    };

    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [params.throttle, params.onResize]);

  return windowSize;
};
